/**
 * packages.js — sections/packages.liquid only.
 *
 * Three responsibilities:
 *   1. WY/DE state toggle — a REAL variant toggle, not a display-only price
 *      swap (the mockup bug called out in the roadmap). Selecting a state
 *      radio rewrites each package card's visible price AND the hidden
 *      `id` input inside its add-to-cart form, so whatever price is shown
 *      is exactly what gets added to the cart. The price figure itself
 *      rolls old→new (P0.1, `animatePriceRoll()`) instead of snapping.
 *   2. Add-to-cart — progressive enhancement over a plain `/cart/add` form
 *      post (works with JS disabled/broken) using the Cart AJAX API
 *      (`/cart/add.js`) with loading + error states, then OPENS THE CART
 *      DRAWER (sections/cart-drawer.liquid). It used to redirect straight to
 *      checkout, because the theme had no cart page and no drawer, so
 *      checkout was the only destination that existed — but that dropped the
 *      shopper onto Shopify's checkout, whose order summary is read-only on a
 *      non-Plus plan, with no way to remove anything added by mistake. The
 *      drawer is that missing step; checkout is one click away inside it.
 *      Before adding, any OTHER package variant already in the cart is zeroed
 *      out, so the total always equals the price on the card they actually
 *      clicked — a shopper who picks a different tier can never end up paying
 *      for two. Add-on line items are deliberately left alone.
 *
 * FAIL-SOFT CONTRACT: every enhancement is additive over working native HTML
 * (radio inputs, a real `<form method="post" action="/cart/add">`). A thrown
 * error or missing `fetch` support degrades to native behavior, never a dead
 * CTA (CODING-STANDARDS "Error Handling Conventions"). The price roll is the
 * same contract: under `prefers-reduced-motion` or if the tween throws, the
 * final price is set directly via `textContent` — never stuck mid-tween.
 *
 * Loaded as `<script type="module" defer>` from sections/packages.liquid —
 * no globals, no dependencies. (Deliberately not importing motion.js's
 * `animateCount` — that function always tweens FROM 0, this needs an
 * arbitrary old→new tween; the two files stay independent per this
 * project's per-section-JS convention, so a failure in one can never break
 * the other.)
 */

const LOG_PREFIX = "[BecomeFounder]";
const PRICE_ROLL_DURATION_MS = 450;
const JSON_HEADERS = {
  "Content-Type": "application/json",
  Accept: "application/json",
};

function prefersReducedMotion() {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * P0.1 — WY/DE toggle number-roll. Tweens `el`'s text from `fromText` to
 * `toText` over `PRICE_ROLL_DURATION_MS`, preserving every non-digit
 * character verbatim (same contract as motion.js's `animateCount`) by
 * using `toText` as the render template and only substituting its digit
 * runs. Digit runs are paired by POSITION between `fromText` and `toText`;
 * a run with no counterpart (either side) starts/stays at 0. This is what
 * produces the acceptable "$1,049" case called out in the design-lead spec:
 * "$649" -> "$1,049" has 1 digit run on the left, 2 on the right (the comma
 * splits them) — the extra run just tweens in from 0 rather than from a
 * real prior value. Always finishes on the exact `toText` string.
 *
 * Shares the `el.__bfAnimToken` convention with motion.js's `animateCount`
 * (both files independently stamp/check it) so if this fires while that
 * function's scroll-triggered count-up on the same element is still
 * mid-flight, the newer animation silently wins instead of the two rAF
 * loops fighting over `textContent` every frame.
 */
function animatePriceRoll(el, fromText, toText) {
  const fromDigits = (fromText.match(/\d+/g) || []).map(Number);
  const toDigits = (toText.match(/\d+/g) || []).map(Number);

  // Skip the tween and commit the final value outright whenever a frame
  // loop can't be trusted to deliver it. The hidden-document case is the
  // load-bearing one: browsers throttle `requestAnimationFrame` to zero in
  // a background tab, so a state toggle would leave the OLD price on screen
  // while `applyState()` had already swapped the hidden variant `id`
  // synchronously — the displayed-price/charged-price mismatch this whole
  // section exists to prevent. Correctness of the money figure outranks the
  // animation every time; the roll is decoration over an always-correct
  // value, never the thing that produces it.
  if (
    !toDigits.length ||
    prefersReducedMotion() ||
    document.hidden ||
    typeof window.requestAnimationFrame !== "function"
  ) {
    el.textContent = toText;
    return;
  }

  const token = (el.__bfAnimToken = (el.__bfAnimToken || 0) + 1);
  const start = performance.now();

  function frame(now) {
    if (el.__bfAnimToken !== token) return; // superseded — stop writing
    const elapsed = now - start;
    const progress = Math.min(elapsed / PRICE_ROLL_DURATION_MS, 1);
    const eased = 1 - Math.pow(1 - progress, 3);

    let i = 0;
    const rendered = toText.replace(/\d+/g, (match) => {
      const to = toDigits[i];
      const from = fromDigits[i] || 0;
      i += 1;
      const current = Math.round(from + (to - from) * eased);
      return String(Math.max(current, 0)).padStart(match.length, "0");
    });
    el.textContent = rendered;

    if (progress < 1) {
      window.requestAnimationFrame(frame);
    } else {
      el.textContent = toText; // guarantee the exact final string
    }
  }

  window.requestAnimationFrame(frame);
}

/**
 * Wires every `[data-state-selector]` fieldset to the package cards inside
 * its own `[data-packages-section]` ancestor (scoped, so multiple instances
 * of the section on one page — however unlikely — don't cross-wire).
 */
function initStateToggle() {
  document.querySelectorAll("[data-state-selector]").forEach((fieldset) => {
    const section = fieldset.closest("[data-packages-section]");
    if (!section) return;

    const radios = fieldset.querySelectorAll('input[type="radio"]');
    if (!radios.length) return;

    function applyState(state, stateName) {
      section.querySelectorAll("[data-package-card]").forEach((card) => {
        // Guard (code-review carry-forward): a card can be flagged
        // `data-no-<state>` when the underlying product/demo block has no
        // variant for that state (e.g. Delaware). Skip the whole card so it
        // stays pinned to its only real state — price, variant id, and the
        // feature label all stay in lockstep instead of a stale label
        // surviving next to a price that quietly never changed.
        if (card.hasAttribute(`data-no-${state}`)) {
          return;
        }

        const price = card.getAttribute(`data-price-${state}`);
        const variantId = card.getAttribute(`data-variant-${state}`);
        const priceEl = card.querySelector("[data-price-display]");
        const variantInput = card.querySelector("[data-variant-input]");

        if (price && priceEl) {
          const currentPrice = priceEl.textContent;
          if (currentPrice !== price) {
            animatePriceRoll(priceEl, currentPrice, price);
            // Keep the count-up-on-first-view attribute in sync so the DOM
            // never disagrees with what's on screen, even though
            // initCounters() already unobserved this element after its one
            // on-scroll run and won't re-read it.
            priceEl.setAttribute("data-count-value", price);
          }

          const label = card.querySelector("[data-state-feature-label]");
          if (label && stateName) {
            label.textContent = `US LLC formation (${stateName})`;
          }
        }
        if (variantId && variantInput) {
          variantInput.value = variantId;
        }
      });

      fieldset.querySelectorAll(".state-card").forEach((card) => {
        const input = card.querySelector('input[type="radio"]');
        card.classList.toggle("is-active", Boolean(input && input.checked));
      });
    }

    radios.forEach((radio) => {
      radio.addEventListener("change", () => {
        if (!radio.checked) return;
        try {
          applyState(radio.value, radio.dataset.stateName);
        } catch (err) {
          console.warn(`${LOG_PREFIX} state toggle failed`, err);
        }
      });
    });
  });
}

/**
 * Progressive-enhancement add-to-cart. If `fetch` isn't available, this
 * function does nothing and the form's native `action="/cart/add"` POST
 * still works (full page navigation to the cart-add redirect).
 */
function initAddToCart() {
  if (typeof window.fetch !== "function") {
    return;
  }

  document.querySelectorAll("[data-package-form]").forEach((form) => {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      handleAddToCart(form).catch((err) => {
        console.warn(`${LOG_PREFIX} add-to-cart handler failed`, err);
      });
    });
  });
}

/**
 * Every package variant rendered in this section, across all states —
 * read straight off the cards' `data-variant-<state>` attributes rather
 * than duplicated into a separate Liquid list, so the two can't disagree.
 * (`data-variant-input` lives on the hidden input, not the card, so it
 * can't collide with the prefix match.)
 */
function packageVariantIds(section) {
  const ids = new Set();
  if (!section) return ids;
  section.querySelectorAll("[data-package-card]").forEach((card) => {
    Array.from(card.attributes).forEach((attr) => {
      if (attr.name.startsWith("data-variant-") && attr.value) {
        ids.add(attr.value);
      }
    });
  });
  return ids;
}

/**
 * Set the cart to EXACTLY one of this section's package variants: `keepId` at
 * quantity 1, every other tier at 0. Packages are mutually exclusive — you buy
 * one LLC tier, not three.
 *
 * ONE ATOMIC ABSOLUTE-QUANTITY CALL, deliberately. This replaced a
 * "/cart/update.js to zero the others, then /cart/add.js to add this one" pair
 * that had two money bugs (code review 2026-07-29):
 *
 *   1. `fetch` RESOLVES on 4xx/5xx — it only rejects on network failure. The
 *      old code awaited the update without checking `res.ok`, so a 429 from
 *      Shopify's Cart API rate limiter (entirely plausible on paid traffic)
 *      fell straight through to the add and the shopper ended up holding two
 *      tiers — the exact outcome this function exists to prevent, silently.
 *   2. `/cart/add.js` INCREMENTS. The old code skipped `keepId` when zeroing,
 *      so clicking Add to Cart twice on the SAME package gave quantity 2
 *      (2 x $449). Nothing in the UI signalled that the second click was
 *      destructive.
 *
 * `/cart/update.js` sets absolute quantities and will create a line that isn't
 * present yet, so one call both de-dupes and adds, and repeating it is
 * idempotent. `res.ok` is now checked and the error propagates — a failure
 * here MUST block the add rather than warn, because "couldn't de-dupe" and
 * "charged for two packages" are the same event.
 */
async function setPackageSelection(section, keepId) {
  const updates = {};
  packageVariantIds(section).forEach((id) => {
    updates[id] = id === keepId ? 1 : 0;
  });
  // keepId may not be among this section's rendered cards (defensive).
  updates[keepId] = 1;

  const response = await fetch("/cart/update.js", {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify({ updates }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.description || "Could not add this package to your cart.");
  }
  return response.json();
}

async function handleAddToCart(form) {
  const button = form.querySelector("[data-add-to-cart]");
  const status = form.querySelector("[data-form-status]");
  const variantInput = form.querySelector('[name="id"]');
  const variantId = variantInput ? variantInput.value : "";

  if (!variantId) {
    setStatus(status, "This package isn't available yet — please check back soon.");
    return;
  }

  setLoading(button, true);
  setStatus(status, "");

  try {
    // One atomic absolute-quantity write: sets this tier to 1 and every other
    // tier to 0 in a single request. Replaces the old update-then-add pair —
    // see setPackageSelection() for the two money bugs that caused.
    await setPackageSelection(form.closest("[data-packages-section]"), variantId);

    // Opens the cart drawer instead of jumping straight to checkout.
    //
    // This REVERSES the 2026-07-26 "Add to Cart goes straight to checkout"
    // decision, deliberately: that decision was made when the theme had no
    // cart page and no drawer, so checkout was the only destination that
    // existed. It also meant a shopper landed on Shopify's checkout — whose
    // order summary is READ-ONLY on a non-Plus plan — with no way to remove
    // anything they'd added by mistake, which is the bug this drawer fixes.
    // Checkout is now one deliberate click away, inside the drawer.
    setStatus(status, "Added to cart.");
    resetButtonLabel(button);
    setLoading(button, false);
    document.dispatchEvent(new CustomEvent("cart:updated"));
  } catch (err) {
    console.warn(`${LOG_PREFIX} add-to-cart failed`, err);
    setStatus(status, "Something went wrong — please try again.");
    resetButtonLabel(button);
    setLoading(button, false);
  }
}

function setLoading(button, isLoading) {
  if (!button) return;
  if (isLoading) {
    button.dataset.originalText = button.dataset.originalText || button.textContent;
    button.disabled = true;
    button.setAttribute("aria-busy", "true");
    button.textContent = "Adding…";
  } else {
    button.disabled = false;
    button.removeAttribute("aria-busy");
  }
}

function resetButtonLabel(button) {
  if (button && button.dataset.originalText) {
    button.textContent = button.dataset.originalText;
  }
}

function setStatus(status, message) {
  if (!status) return;
  status.textContent = message;
  status.classList.toggle("visually-hidden", message === "");
}

function init() {
  try {
    initStateToggle();
    initAddToCart();
  } catch (err) {
    console.warn(`${LOG_PREFIX} packages.js init failed — continuing without enhancement`, err);
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
